import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';

export interface CardOptionsMenuItem {
  label: string;
  /** Iconify class, e.g. 'icon-[material-symbols--delete-outline]' */
  iconifyClass?: string;
  /** Destructive actions get error styling */
  intent?: 'default' | 'danger';
  disabled?: boolean;
  onClick: () => void;
}

export interface CardOptionsMenuProps {
  items: CardOptionsMenuItem[];
  /** Accessible label for the trigger button. Defaults to 'Weitere Optionen' */
  ariaLabel?: string;
}

/**
 * A compact "kebab" options menu for advanced / rarely used actions on a card or list entry.
 * Renders nothing if no items are given, so callers can pass a permission-filtered list.
 */
export const CardOptionsMenu = (props: CardOptionsMenuProps) => {
  if (!props.items.length) return nothing;

  const handleClick = (item: CardOptionsMenuItem) => {
    if (item.disabled) return;
    // close the focus-based daisyUI dropdown before running the action
    (document.activeElement as HTMLElement | null)?.blur();
    item.onClick();
  };

  return html`
    <div class="dropdown dropdown-end">
      <button
        type="button"
        tabindex="0"
        class="btn btn-ghost btn-sm btn-square"
        aria-haspopup="true"
        aria-label=${props.ariaLabel ?? 'Weitere Optionen'}
      >
        <span class="icon-[material-symbols--more-vert] h-5 w-5"></span>
      </button>
      <ul tabindex="0" class="dropdown-content menu w-60 p-2">
        ${repeat(
          props.items,
          item => item.label,
          item => html`
            <li class=${item.disabled ? 'menu-disabled' : ''}>
              <button
                type="button"
                class="flex items-center gap-2 ${item.intent === 'danger' ? 'text-error' : ''}"
                @click=${() => handleClick(item)}
              >
                ${item.iconifyClass ? html`<span class="${item.iconifyClass} h-5 w-5 shrink-0"></span>` : nothing}
                <span>${item.label}</span>
              </button>
            </li>
          `,
        )}
      </ul>
    </div>
  `;
};
